import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { settingsAPI, authAPI } from '../utils/api';
import { useConfirm } from '../hooks/useConfirm';
import { FiSave, FiSettings, FiMail, FiCreditCard, FiGlobe, FiEye, FiUpload, FiTrash2, FiPlus, FiEdit2, FiBox, FiUser, FiLock, FiUsers, FiShield, FiCheck, FiX, FiDollarSign } from 'react-icons/fi';
import './Settings.css';

const Settings = () => {
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [settings, setSettings] = useState({
    company_name: '',
    company_name_arabic: '',
    company_address: '',
    company_city: '',
    company_country: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    company_logo: '',
    default_currency: 'AED',
    default_payment_terms: '30',
    default_tax_rate: '5',
    tax_enabled: 'true',
    invoice_prefix: 'INV-',
    invoice_start_number: '1001',
    email_notifications: 'true',
    // Email Settings
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    smtp_secure: 'false',
    email_from: '',
    // Payment Gateway Settings
    tap_api_key: '',
    tap_redirect_url: '',
    stripe_public_key: '',
    stripe_secret_key: '',
    // Regional Settings
    default_country_code: '971',
    timezone: 'Asia/Dubai',
    date_format: 'DD/MM/YYYY',
  });

  // Display fields state with default values (all visible by default)
  const [displayFields, setDisplayFields] = useState({
    sku: true,
    category: true,
    item_name: true,
    description: true,
    unit_price: true,
    unit: true,
    quantity: true,
    tax: true,
  });

  const fileInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Units state
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({ name: '', abbreviation: '' });
  const [savingUnit, setSavingUnit] = useState(false);

  // User profile state
  const [user, setUser] = useState(null);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [teamForm, setTeamForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    role: 'staff'
  });
  const [savingTeamMember, setSavingTeamMember] = useState(false);

  // Permissions state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingPermissionsFor, setEditingPermissionsFor] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_name: '',
    account_number: '',
    iban: '',
    swift_code: '',
    branch_address: '',
    is_default: false
  });
  const [savingBank, setSavingBank] = useState(false);

  // Check if current user is admin
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchSettings();
    fetchUserProfile();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      const settingsObj = {};
      Object.keys(response.data.settings).forEach(key => {
        settingsObj[key] = response.data.settings[key].value;
      });
      setSettings(prev => ({ ...prev, ...settingsObj }));
      
      // Load display fields from settings
      if (settingsObj.display_fields) {
        try {
          const parsed = typeof settingsObj.display_fields === 'string' 
            ? JSON.parse(settingsObj.display_fields) 
            : settingsObj.display_fields;
          setDisplayFields(parsed);
        } catch (e) {
          console.error('Failed to parse display_fields:', e);
        }
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked.toString() : value,
    }));
  };

  const handleDisplayFieldChange = (field) => {
    setDisplayFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (SVG, PNG, JPG, GIF)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await settingsAPI.uploadLogo(formData);
      setSettings(prev => ({ ...prev, company_logo: response.data.logoUrl }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await settingsAPI.update('company_logo', { value: '' });
      setSettings(prev => ({ ...prev, company_logo: '' }));
      toast.success('Logo removed successfully');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Include display fields in settings
      const settingsToSave = {
        ...settings,
        display_fields: JSON.stringify(displayFields)
      };
      await settingsAPI.bulkUpdate(settingsToSave);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Units functions
  const fetchUnits = async () => {
    try {
      setLoadingUnits(true);
      const response = await settingsAPI.getUnits();
      setUnits(response.data.units || []);
    } catch (error) {
      toast.error('Failed to load units');
    } finally {
      setLoadingUnits(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'units') {
      fetchUnits();
    }
    if (activeTab === 'team') {
      fetchTeamMembers();
    }
    if (activeTab === 'bank-accounts') {
      fetchBankAccounts();
    }
  }, [activeTab]);

  // Bank accounts functions
  const fetchBankAccounts = async () => {
    try {
      setLoadingBankAccounts(true);
      const response = await settingsAPI.getBankAccounts();
      setBankAccounts(response.data.bankAccounts || []);
    } catch (error) {
      toast.error('Failed to load bank accounts');
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  const handleOpenBankModal = (account = null) => {
    if (account) {
      setEditingBank(account);
      setBankForm({
        bank_name: account.bank_name || '',
        account_name: account.account_name || '',
        account_number: account.account_number || '',
        iban: account.iban || '',
        swift_code: account.swift_code || '',
        branch_address: account.branch_address || '',
        is_default: account.is_default || false
      });
    } else {
      setEditingBank(null);
      setBankForm({
        bank_name: '',
        account_name: '',
        account_number: '',
        iban: '',
        swift_code: '',
        branch_address: '',
        is_default: false
      });
    }
    setShowBankModal(true);
  };

  const handleCloseBankModal = () => {
    setShowBankModal(false);
    setEditingBank(null);
    setBankForm({
      bank_name: '',
      account_name: '',
      account_number: '',
      iban: '',
      swift_code: '',
      branch_address: '',
      is_default: false
    });
  };

  const handleBankFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBankForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSaveBankAccount = async (e) => {
    e.preventDefault();
    if (!bankForm.bank_name.trim() || !bankForm.account_name.trim() || !bankForm.account_number.trim()) {
      toast.error('Bank name, account name, and account number are required');
      return;
    }

    setSavingBank(true);
    try {
      if (editingBank) {
        await settingsAPI.updateBankAccount(editingBank.id, bankForm);
        toast.success('Bank account updated successfully');
      } else {
        await settingsAPI.createBankAccount(bankForm);
        toast.success('Bank account created successfully');
      }
      handleCloseBankModal();
      fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save bank account');
    } finally {
      setSavingBank(false);
    }
  };

  const handleDeleteBankAccount = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Bank Account',
      message: 'Are you sure you want to delete this bank account? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await settingsAPI.deleteBankAccount(id);
      toast.success('Bank account deleted successfully');
      fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete bank account');
    }
  };

  const handleSetDefaultBankAccount = async (id) => {
    try {
      await settingsAPI.setDefaultBankAccount(id);
      toast.success('Default bank account updated');
      fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to set default bank account');
    }
  };

  // Team management functions
  const fetchTeamMembers = async () => {
    try {
      setLoadingTeam(true);
      const response = await authAPI.getUsers();
      setTeamMembers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleOpenTeamModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setTeamForm({
        email: member.email || '',
        firstName: member.first_name || '',
        lastName: member.last_name || '',
        password: '',
        role: member.role || 'staff'
      });
    } else {
      setEditingMember(null);
      setTeamForm({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        role: 'staff'
      });
    }
    setShowTeamModal(true);
  };

  const handleCloseTeamModal = () => {
    setShowTeamModal(false);
    setEditingMember(null);
    setTeamForm({
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      role: 'staff'
    });
  };

  const handleTeamFormChange = (e) => {
    const { name, value } = e.target;
    setTeamForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveTeamMember = async (e) => {
    e.preventDefault();
    
    if (!teamForm.email || !teamForm.firstName || !teamForm.lastName) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!editingMember && !teamForm.password) {
      toast.error('Password is required for new users');
      return;
    }

    if (!editingMember && teamForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // Validate password complexity for new users
    if (!editingMember) {
      const hasUpperCase = /[A-Z]/.test(teamForm.password);
      const hasLowerCase = /[a-z]/.test(teamForm.password);
      const hasNumber = /[0-9]/.test(teamForm.password);
      
      if (!hasUpperCase || !hasLowerCase || !hasNumber) {
        toast.error('Password must contain at least one uppercase letter, one lowercase letter, and one number');
        return;
      }
    }

    setSavingTeamMember(true);
    try {
      if (editingMember) {
        // Update existing user role
        await authAPI.updateUserRole(editingMember.id, teamForm.role);
        toast.success('Team member updated successfully');
      } else {
        // Create new user
        console.log('Creating user with data:', {
          email: teamForm.email,
          firstName: teamForm.firstName,
          lastName: teamForm.lastName,
          role: teamForm.role
        });
        await authAPI.register({
          email: teamForm.email,
          firstName: teamForm.firstName,
          lastName: teamForm.lastName,
          password: teamForm.password,
          role: teamForm.role
        });
        toast.success('Team member added successfully');
      }
      handleCloseTeamModal();
      fetchTeamMembers();
    } catch (error) {
      console.error('Save team member error:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.error || 
        (error.response?.data?.errors && error.response.data.errors[0]?.msg) ||
        error.message ||
        'Failed to save team member';
      toast.error(errorMessage);
    } finally {
      setSavingTeamMember(false);
    }
  };

  const handleToggleUserStatus = async (member) => {
    const newStatus = !member.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    const confirmed = await confirm({
      title: `${newStatus ? 'Activate' : 'Deactivate'} User`,
      message: `Are you sure you want to ${action} ${member.first_name} ${member.last_name}?`,
      type: newStatus ? 'info' : 'warning',
      confirmText: newStatus ? 'Activate' : 'Deactivate',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await authAPI.updateUserStatus(member.id, newStatus);
      toast.success(`User ${action}d successfully`);
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${action} user`);
    }
  };

  const handleDeleteTeamMember = async (member) => {
    const confirmed = await confirm({
      title: 'Delete User',
      message: `Are you sure you want to delete ${member.first_name} ${member.last_name}? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await authAPI.deleteUser(member.id);
      toast.success('User deleted successfully');
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  // Permissions functions
  const handleOpenPermissionsModal = async (member) => {
    if (member.role === 'admin') {
      toast.error('Admin users have full access and cannot have custom permissions');
      return;
    }
    
    setEditingPermissionsFor(member);
    
    // Fetch available permissions if not loaded
    if (!availablePermissions) {
      try {
        const response = await authAPI.getPermissionsList();
        setAvailablePermissions(response.data.permissions);
      } catch (error) {
        toast.error('Failed to load permissions list');
        return;
      }
    }
    
    // Set user's current permissions or defaults based on role
    const defaultPermissions = getDefaultPermissionsForRole(member.role);
    setUserPermissions(member.permissions || defaultPermissions);
    
    setShowPermissionsModal(true);
  };

  const getDefaultPermissionsForRole = (role) => {
    switch (role) {
      case 'manager':
        return {
          invoices: { view: true, create: true, edit: true, delete: false, send: true, export: true },
          quotations: { view: true, create: true, edit: true, delete: false, send: true, convert: true },
          clients: { view: true, create: true, edit: true, delete: false },
          items: { view: true, create: true, edit: true, delete: false },
          payments: { view: true, create: true, edit: false, delete: false },
          reports: { view: true, export: true },
          settings: { view: false, edit: false }
        };
      case 'staff':
      default:
        return {
          invoices: { view: true, create: false, edit: false, delete: false, send: false, export: false },
          quotations: { view: true, create: false, edit: false, delete: false, send: false, convert: false },
          clients: { view: true, create: false, edit: false, delete: false },
          items: { view: true, create: false, edit: false, delete: false },
          payments: { view: true, create: false, edit: false, delete: false },
          reports: { view: true, export: false },
          settings: { view: false, edit: false }
        };
    }
  };

  const handleClosePermissionsModal = () => {
    setShowPermissionsModal(false);
    setEditingPermissionsFor(null);
    setUserPermissions({});
  };

  const handlePermissionChange = (category, permission) => {
    setUserPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permission]: !prev[category]?.[permission]
      }
    }));
  };

  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      await authAPI.updateUserPermissions(editingPermissionsFor.id, userPermissions);
      toast.success('Permissions updated successfully');
      handleClosePermissionsModal();
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'badge-danger';
      case 'manager': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const handleOpenUnitModal = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setUnitForm({ name: unit.name, abbreviation: unit.abbreviation || '' });
    } else {
      setEditingUnit(null);
      setUnitForm({ name: '', abbreviation: '' });
    }
    setShowUnitModal(true);
  };

  const handleCloseUnitModal = () => {
    setShowUnitModal(false);
    setEditingUnit(null);
    setUnitForm({ name: '', abbreviation: '' });
  };

  const handleUnitFormChange = (e) => {
    const { name, value } = e.target;
    setUnitForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveUnit = async (e) => {
    e.preventDefault();
    if (!unitForm.name.trim()) {
      toast.error('Unit name is required');
      return;
    }

    setSavingUnit(true);
    try {
      if (editingUnit) {
        const response = await settingsAPI.updateUnit(editingUnit.id, unitForm);
        console.log('Update unit response:', response);
        toast.success('Unit updated successfully');
      } else {
        const response = await settingsAPI.createUnit(unitForm);
        console.log('Create unit response:', response);
        toast.success('Unit created successfully');
      }
      handleCloseUnitModal();
      fetchUnits();
    } catch (error) {
      console.error('Save unit error:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save unit';
      toast.error(errorMessage);
    } finally {
      setSavingUnit(false);
    }
  };

  const handleDeleteUnit = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Unit',
      message: 'Are you sure you want to delete this unit? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await settingsAPI.deleteUnit(id);
      toast.success('Unit deleted successfully');
      fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete unit');
    }
  };

  // User profile functions
  const fetchUserProfile = async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data.user;
      console.log('User profile loaded:', userData);
      setUser(userData);
      setProfileForm({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || ''
      });
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      toast.error('Failed to load user profile');
    }
  };

  const handleProfileFormChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await authAPI.updateProfile({
        firstName: profileForm.firstName,
        lastName: profileForm.lastName
      });
      toast.success('Profile updated successfully');
      // Update local storage user
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({
        ...storedUser,
        firstName: profileForm.firstName,
        lastName: profileForm.lastName
      }));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.updateProfile({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Settings</h1>
          <p>Configure your invoicing system</p>
        </div>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <button
            className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <FiUser /> Profile
          </button>
          <button
            className={`settings-tab ${activeTab === 'company' ? 'active' : ''}`}
            onClick={() => setActiveTab('company')}
          >
            <FiSettings /> Company Info
          </button>
          <button
            className={`settings-tab ${activeTab === 'invoice' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoice')}
          >
            <FiSettings /> Invoice Settings
          </button>
          <button
            className={`settings-tab ${activeTab === 'display-fields' ? 'active' : ''}`}
            onClick={() => setActiveTab('display-fields')}
          >
            <FiEye /> Display Fields
          </button>
          <button
            className={`settings-tab ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => setActiveTab('email')}
          >
            <FiMail /> Email Settings
          </button>
          <button
            className={`settings-tab ${activeTab === 'payment' ? 'active' : ''}`}
            onClick={() => setActiveTab('payment')}
          >
            <FiCreditCard /> Payment Gateways
          </button>
          <button
            className={`settings-tab ${activeTab === 'regional' ? 'active' : ''}`}
            onClick={() => setActiveTab('regional')}
          >
            <FiGlobe /> Regional
          </button>
          <button
            className={`settings-tab ${activeTab === 'units' ? 'active' : ''}`}
            onClick={() => setActiveTab('units')}
          >
            <FiBox /> Units
          </button>
          {isAdmin && (
            <button
              className={`settings-tab ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              <FiUsers /> Team
            </button>
          )}
          <button
            className={`settings-tab ${activeTab === 'bank-accounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('bank-accounts')}
          >
            <FiDollarSign /> Bank Accounts
          </button>
        </div>

        <div className="settings-content">
          <form onSubmit={handleSubmit}>
            {activeTab === 'profile' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Profile Settings</h3>
                </div>
                <div className="card-body">
                  <p className="text-muted" style={{ marginBottom: '20px' }}>
                    Manage your account details and password.
                  </p>
                  
                  {/* Profile Information */}
                  <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                    <FiUser /> Account Information
                  </h4>
                  <form onSubmit={handleProfileSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">First Name</label>
                        <input
                          type="text"
                          name="firstName"
                          value={profileForm.firstName}
                          onChange={handleProfileFormChange}
                          className="form-control"
                          placeholder="Enter your first name"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last Name</label>
                        <input
                          type="text"
                          name="lastName"
                          value={profileForm.lastName}
                          onChange={handleProfileFormChange}
                          className="form-control"
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={profileForm.email}
                        className="form-control"
                        disabled
                        style={{ backgroundColor: '#f3f4f6' }}
                      />
                      <span className="form-hint">Email address cannot be changed</span>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={savingProfile}
                    >
                      {savingProfile ? (
                        <>
                          <span className="loading-spinner"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave /> Save Profile
                        </>
                      )}
                    </button>
                  </form>

                  {/* Change Password */}
                  <h4 style={{ marginTop: '40px', marginBottom: '20px', color: 'var(--text-primary)' }}>
                    <FiLock /> Change Password
                  </h4>
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="form-group">
                      <label className="form-label">Current Password</label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordFormChange}
                        className="form-control"
                        placeholder="Enter your current password"
                        required
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">New Password</label>
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordFormChange}
                          className="form-control"
                          placeholder="Enter new password"
                          minLength="8"
                          required
                        />
                        <span className="form-hint">Minimum 8 characters with uppercase, lowercase, and number</span>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Confirm New Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordFormChange}
                          className="form-control"
                          placeholder="Confirm new password"
                          required
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-secondary"
                      disabled={savingPassword}
                    >
                      {savingPassword ? (
                        <>
                          <span className="loading-spinner"></span>
                          Changing...
                        </>
                      ) : (
                        <>
                          <FiLock /> Change Password
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'company' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Company Information</h3>
                </div>
                <div className="card-body">
                  {/* Logo Upload Section */}
                  <div className="form-group">
                    <label className="form-label">Company Logo</label>
                    <p className="text-muted" style={{ marginBottom: '12px', fontSize: '0.875rem' }}>
                      Upload your company logo to display on invoices and PDF exports. Recommended size: 250x80px. Max file size: 2MB.
                    </p>
                    <div className="logo-upload-container">
                      {settings.company_logo ? (
                        <div className="logo-preview">
                          <img src={settings.company_logo} alt="Company Logo" />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={handleRemoveLogo}
                          >
                            <FiTrash2 /> Remove
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="logo-upload-area"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <FiUpload className="upload-icon" />
                          <span>Click to upload logo</span>
                          <span className="upload-hint">SVG, PNG, JPG or GIF (max 2MB)</span>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/svg+xml,image/png,image/jpeg,image/jpg,image/gif"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                      />
                    </div>
                    {uploadingLogo && (
                      <div className="uploading-indicator">
                        <span className="loading-spinner"></span>
                        <span>Uploading...</span>
                      </div>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Company Name (English)</label>
                      <input
                        type="text"
                        name="company_name"
                        value={settings.company_name}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Your Company Name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Company Name (Arabic)</label>
                      <input
                        type="text"
                        name="company_name_arabic"
                        value={settings.company_name_arabic}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="اسم الشركة بالعربية"
                        dir="rtl"
                        style={{ textAlign: 'right' }}
                      />
                      <span className="form-hint">Displayed under the English name on public invoices and PDFs</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      name="company_address"
                      value={settings.company_address}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Street address"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        name="company_city"
                        value={settings.company_city}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="City"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        name="company_country"
                        value={settings.company_country}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Country"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        name="company_phone"
                        value={settings.company_phone}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="+971 4 123 4567"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Website</label>
                      <input
                        type="url"
                        name="company_website"
                        value={settings.company_website}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="www.company.com"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'invoice' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Invoice Settings</h3>
                </div>
                <div className="card-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Invoice Prefix</label>
                      <input
                        type="text"
                        name="invoice_prefix"
                        value={settings.invoice_prefix}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="INV-"
                      />
                      <span className="form-hint">Prefix for invoice numbers (e.g., INV-000001)</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Starting Number</label>
                      <input
                        type="number"
                        name="invoice_start_number"
                        value={settings.invoice_start_number}
                        onChange={handleChange}
                        className="form-control"
                        min="1"
                      />
                      <span className="form-hint">Starting number for invoices</span>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Default Currency</label>
                      <select
                        name="default_currency"
                        value={settings.default_currency}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="AED">AED - UAE Dirham</option>
                        <option value="USD">USD - US Dollar</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="GBP">GBP - British Pound</option>
                        <option value="SAR">SAR - Saudi Riyal</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Payment Terms (Days)</label>
                      <input
                        type="number"
                        name="default_payment_terms"
                        value={settings.default_payment_terms}
                        onChange={handleChange}
                        className="form-control"
                        min="0"
                      />
                      <span className="form-hint">Default payment terms for new invoices</span>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Default Tax Rate (%)</label>
                      <input
                        type="number"
                        name="default_tax_rate"
                        value={settings.default_tax_rate}
                        onChange={handleChange}
                        className="form-control"
                        min="0"
                        max="100"
                        step="0.01"
                        disabled={settings.tax_enabled !== 'true'}
                      />
                      <span className="form-hint">Default tax rate for invoice items</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Tax Settings</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="tax_enabled"
                          checked={settings.tax_enabled === 'true'}
                          onChange={handleChange}
                          id="tax_enabled"
                        />
                        <label htmlFor="tax_enabled">Enable tax on invoices</label>
                      </div>
                      <span className="form-hint">Uncheck to disable tax calculations</span>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Email Notifications</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="email_notifications"
                          checked={settings.email_notifications === 'true'}
                          onChange={handleChange}
                          id="email_notifications"
                        />
                        <label htmlFor="email_notifications">Send email notifications for invoice events</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'display-fields' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Display Fields</h3>
                </div>
                <div className="card-body">
                  <p className="text-muted" style={{ marginBottom: '20px' }}>
                    Control which fields are displayed in the invoice public link and PDF export. Toggle each field to show or hide it.
                  </p>
                  
                  <div className="display-fields-grid">
                    <div className="display-field-item">
                      <div className="display-field-info">
                        <span className="display-field-label">SKU</span>
                        <span className="display-field-description">Show the item SKU/code column</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={displayFields.sku}
                          onChange={() => handleDisplayFieldChange('sku')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="display-field-item">
                      <div className="display-field-info">
                        <span className="display-field-label">Category</span>
                        <span className="display-field-description">Show the item category column</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={displayFields.category}
                          onChange={() => handleDisplayFieldChange('category')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="display-field-item">
                      <div className="display-field-info">
                        <span className="display-field-label">Item Name</span>
                        <span className="display-field-description">Show the item name column</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={displayFields.item_name}
                          onChange={() => handleDisplayFieldChange('item_name')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="display-field-item">
                      <div className="display-field-info">
                        <span className="display-field-label">Description</span>
                        <span className="display-field-description">Show the item description column</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={displayFields.description}
                          onChange={() => handleDisplayFieldChange('description')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="display-field-item">
                      <div className="display-field-info">
                        <span className="display-field-label">Unit Price</span>
                        <span className="display-field-description">Show the unit price column</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={displayFields.unit_price}
                          onChange={() => handleDisplayFieldChange('unit_price')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="display-field-item">
                      <div className="display-field-info">
                        <span className="display-field-label">Unit</span>
                        <span className="display-field-description">Show the unit of measurement column</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={displayFields.unit}
                          onChange={() => handleDisplayFieldChange('unit')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="display-field-item">
                      <div className="display-field-info">
                        <span className="display-field-label">Quantity</span>
                        <span className="display-field-description">Show the quantity column</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={displayFields.quantity}
                          onChange={() => handleDisplayFieldChange('quantity')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="display-field-item">
                      <div className="display-field-info">
                        <span className="display-field-label">Tax</span>
                        <span className="display-field-description">Show the tax column</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={displayFields.tax}
                          onChange={() => handleDisplayFieldChange('tax')}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'email' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Email Settings (SMTP)</h3>
                </div>
                <div className="card-body">
                  <p className="text-muted" style={{ marginBottom: '20px' }}>
                    Configure your SMTP settings to send invoices via email. For Gmail, you'll need to enable 2FA and create an App Password.
                  </p>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">SMTP Host</label>
                      <input
                        type="text"
                        name="smtp_host"
                        value={settings.smtp_host}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="smtp.gmail.com"
                      />
                      <span className="form-hint">e.g., smtp.gmail.com, smtp.office365.com</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">SMTP Port</label>
                      <input
                        type="text"
                        name="smtp_port"
                        value={settings.smtp_port}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="587"
                      />
                      <span className="form-hint">Usually 587 for TLS, 465 for SSL</span>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">SMTP Username</label>
                      <input
                        type="text"
                        name="smtp_user"
                        value={settings.smtp_user}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="your-email@gmail.com"
                      />
                      <span className="form-hint">Your email address</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">SMTP Password</label>
                      <input
                        type="password"
                        name="smtp_password"
                        value={settings.smtp_password}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="••••••••"
                      />
                      <span className="form-hint">For Gmail, use an App Password</span>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">From Email</label>
                      <input
                        type="email"
                        name="email_from"
                        value={settings.email_from}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="noreply@yourcompany.com"
                      />
                      <span className="form-hint">Email address shown as sender</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Security</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="smtp_secure"
                          checked={settings.smtp_secure === 'true'}
                          onChange={handleChange}
                          id="smtp_secure"
                        />
                        <label htmlFor="smtp_secure">Use SSL/TLS</label>
                      </div>
                      <span className="form-hint">Enable for port 465 (SSL)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'payment' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Payment Gateway Settings</h3>
                </div>
                <div className="card-body">
                  <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>Tap Payments</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Tap API Key</label>
                      <input
                        type="text"
                        name="tap_api_key"
                        value={settings.tap_api_key}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="sk_test_..."
                      />
                      <span className="form-hint">Your Tap Payments secret key</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Tap Redirect URL</label>
                      <input
                        type="url"
                        name="tap_redirect_url"
                        value={settings.tap_redirect_url}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="http://localhost:3000/payment/callback"
                      />
                      <span className="form-hint">URL to redirect after payment</span>
                    </div>
                  </div>

                  <h4 style={{ marginTop: '30px', marginBottom: '20px', color: 'var(--text-primary)' }}>Stripe</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Stripe Publishable Key</label>
                      <input
                        type="text"
                        name="stripe_public_key"
                        value={settings.stripe_public_key}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="pk_test_..."
                      />
                      <span className="form-hint">Your Stripe publishable key</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Stripe Secret Key</label>
                      <input
                        type="password"
                        name="stripe_secret_key"
                        value={settings.stripe_secret_key}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="sk_test_..."
                      />
                      <span className="form-hint">Your Stripe secret key</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'regional' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Regional Settings</h3>
                </div>
                <div className="card-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Default Country Code</label>
                      <select
                        name="default_country_code"
                        value={settings.default_country_code}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="971">+971 - UAE</option>
                        <option value="966">+966 - Saudi Arabia</option>
                        <option value="965">+965 - Kuwait</option>
                        <option value="974">+974 - Qatar</option>
                        <option value="973">+973 - Bahrain</option>
                        <option value="968">+968 - Oman</option>
                        <option value="20">+20 - Egypt</option>
                        <option value="962">+962 - Jordan</option>
                        <option value="961">+961 - Lebanon</option>
                        <option value="1">+1 - USA/Canada</option>
                        <option value="44">+44 - UK</option>
                      </select>
                      <span className="form-hint">Default country code for phone numbers (WhatsApp)</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Timezone</label>
                      <select
                        name="timezone"
                        value={settings.timezone}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="Asia/Dubai">Asia/Dubai (GMT+4)</option>
                        <option value="Asia/Riyadh">Asia/Riyadh (GMT+3)</option>
                        <option value="Asia/Kuwait">Asia/Kuwait (GMT+3)</option>
                        <option value="Asia/Qatar">Asia/Qatar (GMT+3)</option>
                        <option value="Asia/Bahrain">Asia/Bahrain (GMT+3)</option>
                        <option value="Asia/Muscat">Asia/Muscat (GMT+4)</option>
                        <option value="Africa/Cairo">Africa/Cairo (GMT+2)</option>
                        <option value="Europe/London">Europe/London (GMT+0/+1)</option>
                        <option value="America/New_York">America/New_York (GMT-5/-4)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Date Format</label>
                      <select
                        name="date_format"
                        value={settings.date_format}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
                        <option value="DD-MM-YYYY">DD-MM-YYYY (31-12-2024)</option>
                      </select>
                      <span className="form-hint">How dates are displayed throughout the system</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'units' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Units of Measurement</h3>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOpenUnitModal()}
                  >
                    <FiPlus /> Add Unit
                  </button>
                </div>
                <div className="card-body">
                  <p className="text-muted" style={{ marginBottom: '20px' }}>
                    Manage units of measurement for items in invoices. These units will be available when creating or editing items.
                  </p>

                  {loadingUnits ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                    </div>
                  ) : units.length === 0 ? (
                    <div className="empty-state">
                      <FiBox className="empty-icon" />
                      <h3>No Units Yet</h3>
                      <p>Add your first unit of measurement to get started.</p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleOpenUnitModal()}
                      >
                        <FiPlus /> Add Unit
                      </button>
                    </div>
                  ) : (
                    <div className="units-table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Unit Name</th>
                            <th>Abbreviation</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {units.map((unit) => (
                            <tr key={unit.id}>
                              <td><strong>{unit.name}</strong></td>
                              <td>{unit.abbreviation || '-'}</td>
                              <td>
                                <span className={`badge ${unit.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                  {unit.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleOpenUnitModal(unit)}
                                  style={{ marginRight: '8px' }}
                                >
                                  <FiEdit2 />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteUnit(unit.id)}
                                >
                                  <FiTrash2 />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Team Management Tab */}
            {activeTab === 'team' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Team Members</h3>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOpenTeamModal()}
                  >
                    <FiPlus /> Add Team Member
                  </button>
                </div>
                <div className="card-body">
                  <p className="text-muted" style={{ marginBottom: '20px' }}>
                    Manage your team members and their roles. Admins have full access, Managers can create and edit invoices, Staff can only view.
                  </p>

                  {loadingTeam ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div className="empty-state">
                      <FiUsers className="empty-icon" />
                      <h3>No Team Members</h3>
                      <p>Add your first team member to get started.</p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleOpenTeamModal()}
                      >
                        <FiPlus /> Add Team Member
                      </button>
                    </div>
                  ) : (
                    <div className="team-table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamMembers.map((member) => (
                            <tr key={member.id} className={!member.is_active ? 'inactive-row' : ''}>
                              <td>
                                <div className="member-name">
                                  <strong>{member.first_name} {member.last_name}</strong>
                                  {member.id === user?.id && (
                                    <span className="badge badge-primary" style={{ marginLeft: '8px' }}>You</span>
                                  )}
                                </div>
                              </td>
                              <td>{member.email}</td>
                              <td>
                                <span className={`badge ${getRoleBadgeClass(member.role)}`}>
                                  {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${member.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                  {member.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {member.id !== user?.id && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => handleOpenTeamModal(member)}
                                      style={{ marginRight: '8px' }}
                                      title="Edit role"
                                    >
                                      <FiEdit2 />
                                    </button>
                                    {member.role !== 'admin' && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-info"
                                        onClick={() => handleOpenPermissionsModal(member)}
                                        style={{ marginRight: '8px' }}
                                        title="Edit permissions"
                                      >
                                        <FiShield />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className={`btn btn-sm ${member.is_active ? 'btn-warning' : 'btn-success'}`}
                                      onClick={() => handleToggleUserStatus(member)}
                                      style={{ marginRight: '8px' }}
                                      title={member.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                      {member.is_active ? <FiX /> : <FiCheck />}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleDeleteTeamMember(member)}
                                      title="Delete"
                                    >
                                      <FiTrash2 />
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Role Permissions Info */}
                  <div className="role-permissions-info" style={{ marginTop: '30px', padding: '20px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FiShield /> Role Permissions
                    </h4>
                    <div className="permissions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                      <div className="permission-card" style={{ padding: '15px', backgroundColor: 'var(--bg-primary)', borderRadius: '6px' }}>
                        <h5 style={{ color: 'var(--danger-color)', marginBottom: '8px' }}>Admin</h5>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          <li>Full system access</li>
                          <li>Manage team members</li>
                          <li>All settings access</li>
                          <li>Delete any records</li>
                        </ul>
                      </div>
                      <div className="permission-card" style={{ padding: '15px', backgroundColor: 'var(--bg-primary)', borderRadius: '6px' }}>
                        <h5 style={{ color: 'var(--warning-color)', marginBottom: '8px' }}>Manager</h5>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          <li>Create/edit invoices</li>
                          <li>Manage clients</li>
                          <li>View all records</li>
                          <li>Send emails</li>
                        </ul>
                      </div>
                      <div className="permission-card" style={{ padding: '15px', backgroundColor: 'var(--bg-primary)', borderRadius: '6px' }}>
                        <h5 style={{ color: 'var(--info-color)', marginBottom: '8px' }}>Staff</h5>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          <li>View invoices</li>
                          <li>View clients</li>
                          <li>Limited edit access</li>
                          <li>No delete access</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bank Accounts Tab */}
            {activeTab === 'bank-accounts' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Bank Accounts</h3>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOpenBankModal()}
                  >
                    <FiPlus /> Add Bank Account
                  </button>
                </div>
                <div className="card-body">
                  <p className="text-muted" style={{ marginBottom: '20px' }}>
                    Manage your bank accounts for displaying on invoices. Select a bank account when creating an invoice to show its details on the PDF.
                  </p>

                  {loadingBankAccounts ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                    </div>
                  ) : bankAccounts.length === 0 ? (
                    <div className="empty-state">
                      <FiDollarSign className="empty-icon" />
                      <h3>No Bank Accounts</h3>
                      <p>Add your first bank account to display on invoices.</p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleOpenBankModal()}
                      >
                        <FiPlus /> Add Bank Account
                      </button>
                    </div>
                  ) : (
                    <div className="bank-accounts-table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Bank Name</th>
                            <th>Account Name</th>
                            <th>Account Number</th>
                            <th>IBAN</th>
                            <th>Default</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bankAccounts.map((account) => (
                            <tr key={account.id}>
                              <td><strong>{account.bank_name}</strong></td>
                              <td>{account.account_name}</td>
                              <td>{account.account_number}</td>
                              <td>{account.iban || '-'}</td>
                              <td>
                                {account.is_default ? (
                                  <span className="badge badge-success">Default</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => handleSetDefaultBankAccount(account.id)}
                                    title="Set as default"
                                  >
                                    <FiCheck />
                                  </button>
                                )}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleOpenBankModal(account)}
                                  style={{ marginRight: '8px' }}
                                  title="Edit"
                                >
                                  <FiEdit2 />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteBankAccount(account.id)}
                                  title="Delete"
                                >
                                  <FiTrash2 />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Global save applies to settings-backed tabs; tabs that manage
                their own state (Profile, Units, Team, Bank Accounts) have
                their own submit buttons. */}
            {['company', 'invoice', 'display-fields', 'email', 'payment', 'regional'].includes(activeTab) && (
              <div className="card">
                <div className="card-body">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="loading-spinner"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiSave /> Save Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Team Member Modal - OUTSIDE main form */}
          {showTeamModal && (
            <div className="modal-overlay" onClick={handleCloseTeamModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingMember ? 'Edit Team Member' : 'Add New Team Member'}</h3>
                  <button type="button" className="modal-close" onClick={handleCloseTeamModal}>
                    &times;
                  </button>
                </div>
                <form onSubmit={handleSaveTeamMember}>
                  <div className="modal-body">
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          value={teamForm.firstName}
                          onChange={handleTeamFormChange}
                          className="form-control"
                          placeholder="Enter first name"
                          required
                          disabled={editingMember}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          value={teamForm.lastName}
                          onChange={handleTeamFormChange}
                          className="form-control"
                          placeholder="Enter last name"
                          required
                          disabled={editingMember}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={teamForm.email}
                        onChange={handleTeamFormChange}
                        className="form-control"
                        placeholder="Enter email address"
                        required
                        disabled={editingMember}
                      />
                      {editingMember && (
                        <span className="form-hint">Email cannot be changed</span>
                      )}
                    </div>
                    {!editingMember && (
                      <div className="form-group">
                        <label className="form-label">Password *</label>
                        <input
                          type="password"
                          name="password"
                          value={teamForm.password}
                          onChange={handleTeamFormChange}
                          className="form-control"
                          placeholder="Enter password (min 8 characters)"
                          minLength="8"
                          required
                        />
                        <span className="form-hint">Minimum 8 characters with uppercase, lowercase, and number</span>
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">Role *</label>
                      <select
                        name="role"
                        value={teamForm.role}
                        onChange={handleTeamFormChange}
                        className="form-control"
                        required
                      >
                        <option value="staff">Staff - View only</option>
                        <option value="manager">Manager - Create and edit</option>
                        <option value="admin">Admin - Full access</option>
                      </select>
                      <span className="form-hint">
                        {teamForm.role === 'admin' && 'Full system access including team management'}
                        {teamForm.role === 'manager' && 'Can create and edit invoices and clients'}
                        {teamForm.role === 'staff' && 'Can view invoices and clients only'}
                      </span>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseTeamModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={savingTeamMember}
                    >
                      {savingTeamMember ? (
                        <>
                          <span className="loading-spinner"></span>
                          Saving...
                        </>
                      ) : (
                        editingMember ? 'Update Role' : 'Add Team Member'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Unit Modal - OUTSIDE main form */}
          {showUnitModal && (
            <div className="modal-overlay" onClick={handleCloseUnitModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingUnit ? 'Edit Unit' : 'Add New Unit'}</h3>
                  <button type="button" className="modal-close" onClick={handleCloseUnitModal}>
                    &times;
                  </button>
                </div>
                <form onSubmit={handleSaveUnit}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label className="form-label">Unit Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={unitForm.name}
                        onChange={handleUnitFormChange}
                        className="form-control"
                        placeholder="e.g., Piece, Hour, Meter"
                        required
                      />
                      <span className="form-hint">The full name of the unit</span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Abbreviation</label>
                      <input
                        type="text"
                        name="abbreviation"
                        value={unitForm.abbreviation}
                        onChange={handleUnitFormChange}
                        className="form-control"
                        placeholder="e.g., pcs, hr, m"
                      />
                      <span className="form-hint">Short form of the unit (optional)</span>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseUnitModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={savingUnit}
                    >
                      {savingUnit ? (
                        <>
                          <span className="loading-spinner"></span>
                          Saving...
                        </>
                      ) : (
                        editingUnit ? 'Update Unit' : 'Add Unit'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Permissions Modal - OUTSIDE main form */}
          {showPermissionsModal && editingPermissionsFor && (
            <div className="modal-overlay" onClick={handleClosePermissionsModal}>
              <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <FiShield /> Edit Permissions - {editingPermissionsFor.first_name} {editingPermissionsFor.last_name}
                  </h3>
                  <button type="button" className="modal-close" onClick={handleClosePermissionsModal}>
                    &times;
                  </button>
                </div>
                <div className="modal-body">
                  <p className="text-muted" style={{ marginBottom: '20px' }}>
                    Configure granular permissions for this user. Enable or disable specific actions for each module.
                  </p>
                  
                  {availablePermissions && (
                    <div className="permissions-editor">
                      {Object.entries(availablePermissions).map(([category, permissions]) => (
                        <div key={category} className="permission-category">
                          <h4 className="category-title">
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </h4>
                          <div className="permission-items">
                            {Object.entries(permissions).map(([perm, label]) => (
                              <label key={`${category}-${perm}`} className="permission-item">
                                <input
                                  type="checkbox"
                                  checked={userPermissions[category]?.[perm] || false}
                                  onChange={() => handlePermissionChange(category, perm)}
                                />
                                <span className="permission-label">{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClosePermissionsModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSavePermissions}
                    disabled={savingPermissions}
                  >
                    {savingPermissions ? (
                      <>
                        <span className="loading-spinner"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiSave /> Save Permissions
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bank Account Modal - OUTSIDE main form */}
          {showBankModal && (
            <div className="modal-overlay" onClick={handleCloseBankModal}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{editingBank ? 'Edit Bank Account' : 'Add New Bank Account'}</h3>
                  <button type="button" className="modal-close" onClick={handleCloseBankModal}>
                    &times;
                  </button>
                </div>
                <form onSubmit={handleSaveBankAccount}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label className="form-label">Bank Name *</label>
                      <input
                        type="text"
                        name="bank_name"
                        value={bankForm.bank_name}
                        onChange={handleBankFormChange}
                        className="form-control"
                        placeholder="e.g., Emirates NBD, ADCB, HSBC"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Account Name *</label>
                      <input
                        type="text"
                        name="account_name"
                        value={bankForm.account_name}
                        onChange={handleBankFormChange}
                        className="form-control"
                        placeholder="Account holder name"
                        required
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Account Number *</label>
                        <input
                          type="text"
                          name="account_number"
                          value={bankForm.account_number}
                          onChange={handleBankFormChange}
                          className="form-control"
                          placeholder="Account number"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">IBAN</label>
                        <input
                          type="text"
                          name="iban"
                          value={bankForm.iban}
                          onChange={handleBankFormChange}
                          className="form-control"
                          placeholder="AEXX XXXX XXXX XXXX XXXX XXX"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">SWIFT/BIC Code</label>
                        <input
                          type="text"
                          name="swift_code"
                          value={bankForm.swift_code}
                          onChange={handleBankFormChange}
                          className="form-control"
                          placeholder="e.g., EBILAEAD"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Branch Address</label>
                        <input
                          type="text"
                          name="branch_address"
                          value={bankForm.branch_address}
                          onChange={handleBankFormChange}
                          className="form-control"
                          placeholder="Branch address"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-check">
                        <input
                          type="checkbox"
                          name="is_default"
                          checked={bankForm.is_default}
                          onChange={handleBankFormChange}
                        />
                        <span>Set as default bank account</span>
                      </label>
                      <span className="form-hint">Default bank account will be pre-selected on new invoices</span>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseBankModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={savingBank}
                    >
                      {savingBank ? (
                        <>
                          <span className="loading-spinner"></span>
                          Saving...
                        </>
                      ) : (
                        editingBank ? 'Update Bank Account' : 'Add Bank Account'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
